<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisOperationalPage from 'dashboard/components-next/LaisOperationalPage.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import WhatsappCatalogConnectionBar from '../../components/WhatsappCatalogConnectionBar.vue';
import WhatsappCatalogProductForm from '../../components/WhatsappCatalogProductForm.vue';
import { useWhatsappCatalog } from '../../composables/useWhatsappCatalog';
import {
  catalogProductImage,
  catalogProductPrice,
  catalogProductStatus,
} from '../../utils/whatsappCatalog';

const route = useRoute();
const router = useRouter();
const account = useAccount();
const selectedConnectionId = ref(route.query.connection_id || '');
const product = ref(null);
const isLoadingProduct = ref(false);
const pageError = ref('');
const showDeleteConfirm = ref(false);
const initialized = ref(false);

const isCreate = computed(
  () => route.name === 'minha_lais_whatsapp_catalog_new'
);
const isEdit = computed(
  () => route.name === 'minha_lais_whatsapp_catalog_edit'
);
const isDetail = computed(
  () => route.name === 'minha_lais_whatsapp_catalog_show'
);
const pageTitle = computed(() => {
  if (isCreate.value) return 'Adicionar produto';
  if (isEdit.value) return 'Editar produto';
  return product.value?.name || 'Detalhes do produto';
});

const {
  connections,
  isLoadingConnections,
  isSaving,
  error,
  isConnected,
  canManage,
  loadConnections,
  findProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  notifyError,
} = useWhatsappCatalog(selectedConnectionId);

const connectionQuery = computed(() =>
  selectedConnectionId.value
    ? { connection_id: selectedConnectionId.value }
    : {}
);
const listRoute = computed(() =>
  account.accountScopedRoute(
    'minha_lais_whatsapp_catalog_index',
    {},
    connectionQuery.value
  )
);
const connectionRoute = computed(() =>
  account.accountScopedRoute(
    'minha_lais_index',
    {},
    { tab: 'whatsapp', whatsapp_subtab: 'conexao_qr' }
  )
);
const editRoute = computed(() =>
  account.accountScopedRoute(
    'minha_lais_whatsapp_catalog_edit',
    { productId: route.params.productId },
    connectionQuery.value
  )
);
const canEdit = computed(
  () => canManage.value && isConnected.value && Boolean(product.value)
);
const breadcrumb = computed(() => [
  { label: 'Minha Lais', to: account.accountScopedRoute('minha_lais_index') },
  { label: 'WhatsApp' },
  { label: 'Catálogo', to: listRoute.value },
  { label: pageTitle.value },
]);

const syncConnectionQuery = () => {
  const query = { ...route.query };
  if (selectedConnectionId.value)
    query.connection_id = selectedConnectionId.value;
  else delete query.connection_id;
  router.replace({ query }).catch(() => {});
};

const loadProduct = async () => {
  if (isCreate.value || !selectedConnectionId.value) return;

  isLoadingProduct.value = true;
  pageError.value = '';
  try {
    product.value = await findProduct(route.params.productId);
    if (!product.value)
      pageError.value = 'Produto não encontrado nesta conta WhatsApp.';
  } catch (requestError) {
    pageError.value =
      requestError.response?.data?.error ||
      'Não foi possível carregar os detalhes do produto.';
  } finally {
    isLoadingProduct.value = false;
  }
};

const loadPage = async () => {
  await loadConnections();
  initialized.value = true;
  syncConnectionQuery();
  await loadProduct();
};

const handleConnectionChange = async () => {
  if (!initialized.value) return;
  syncConnectionQuery();
  product.value = null;
  await loadProduct();
};

const handleSubmit = async payload => {
  if (!canManage.value || !isConnected.value) return;

  try {
    const savedProduct = isEdit.value
      ? await updateProduct(route.params.productId, payload)
      : await createProduct(payload);
    useAlert(
      isEdit.value
        ? 'Produto atualizado com sucesso.'
        : 'Produto criado com sucesso.'
    );
    const productId = savedProduct.id || route.params.productId;
    if (productId) {
      router.push(
        account.accountScopedRoute(
          'minha_lais_whatsapp_catalog_show',
          { productId },
          connectionQuery.value
        )
      );
    } else {
      router.push(listRoute.value);
    }
  } catch (requestError) {
    notifyError(requestError);
  }
};

const confirmDelete = async () => {
  try {
    await deleteProduct(route.params.productId);
    showDeleteConfirm.value = false;
    useAlert('Produto excluído do catálogo.');
    router.push(listRoute.value);
  } catch (requestError) {
    notifyError(requestError);
  }
};

const cancelForm = () => {
  if (isEdit.value) {
    router.push(
      account.accountScopedRoute(
        'minha_lais_whatsapp_catalog_show',
        { productId: route.params.productId },
        connectionQuery.value
      )
    );
    return;
  }

  router.push(listRoute.value);
};

watch(selectedConnectionId, handleConnectionChange);
onMounted(loadPage);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisOperationalPage
    :title="pageTitle"
    :helper="
      isDetail
        ? 'Consulte os dados sincronizados e escolha a próxima ação.'
        : 'Preencha os dados abaixo para manter o catálogo da conta WhatsApp atualizado.'
    "
    :breadcrumb="breadcrumb"
    show-back
    :loading="isLoadingConnections || isLoadingProduct"
    :error="error || pageError"
    @back="router.push(listRoute)"
    @retry="loadPage"
  >
    <template #actions>
      <LaisPrimaryButton
        v-if="isDetail"
        icon="i-lucide-pencil"
        :disabled="!canEdit"
        @click="router.push(editRoute)"
      >
        Editar produto
      </LaisPrimaryButton>
      <button
        v-if="isDetail"
        type="button"
        class="inline-flex min-h-9 items-center justify-center gap-2 rounded-lais border border-red-200 px-3 text-[12px] font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!canEdit || isSaving"
        @click="showDeleteConfirm = true"
      >
        <span class="i-lucide-trash-2 size-4" />
        Excluir
      </button>
    </template>

    <div class="grid gap-5">
      <WhatsappCatalogConnectionBar
        v-model="selectedConnectionId"
        :connections="connections"
        :connected="isConnected"
        @refresh="loadPage"
      />

      <div
        v-if="!connections.length"
        class="rounded-lais border border-dashed border-lais-border bg-white px-6 py-12 text-center"
      >
        <span
          class="i-lucide-message-circle-off mx-auto block size-10 text-lais-primary"
        />
        <h2 class="mt-3 text-[17px] font-semibold text-lais-text">
          Nenhuma conexão encontrada
        </h2>
        <p
          class="mx-auto mt-2 max-w-lg text-[13px] leading-6 text-lais-text-body"
        >
          Conecte o WhatsApp por QR code antes de administrar o catálogo.
        </p>
        <RouterLink
          :to="connectionRoute"
          class="mt-4 inline-flex h-10 items-center rounded-lais bg-lais-primary px-4 text-[13px] font-semibold text-white"
        >
          Abrir conexão QR
        </RouterLink>
      </div>

      <div
        v-else-if="!isConnected"
        class="rounded-lais border border-amber-200 bg-amber-50 px-6 py-10 text-center"
      >
        <span class="i-lucide-wifi-off mx-auto block size-10 text-amber-700" />
        <h2 class="mt-3 text-[17px] font-semibold text-amber-950">
          Conecte esta sessão para continuar
        </h2>
        <p class="mx-auto mt-2 max-w-lg text-[13px] leading-6 text-amber-900">
          O catálogo é lido e alterado pela conexão WhatsApp Web selecionada.
        </p>
        <RouterLink
          :to="connectionRoute"
          class="mt-4 inline-flex h-10 items-center rounded-lais bg-amber-800 px-4 text-[13px] font-semibold text-white"
        >
          Reconectar WhatsApp
        </RouterLink>
      </div>

      <WhatsappCatalogProductForm
        v-else-if="!isDetail"
        :product="product"
        :is-edit="isEdit"
        :is-saving="isSaving"
        :can-submit="canManage && isConnected"
        @submit="handleSubmit"
        @cancel="cancelForm"
      />

      <div
        v-else-if="product"
        class="grid gap-5 lg:grid-cols-[minmax(260px,420px)_minmax(0,1fr)]"
      >
        <section
          class="overflow-hidden rounded-lais border border-lais-border-soft bg-white"
        >
          <div class="aspect-square bg-lais-bg-card">
            <img
              v-if="catalogProductImage(product)"
              :src="catalogProductImage(product)"
              :alt="product.name"
              class="h-full w-full object-cover"
            />
            <div
              v-else
              class="grid h-full place-items-center text-lais-text-muted"
            >
              <span class="i-lucide-image size-12" />
            </div>
          </div>
          <div class="border-t border-lais-border-soft p-5">
            <span
              class="inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold"
              :class="catalogProductStatus(product).className"
            >
              {{ catalogProductStatus(product).label }}
            </span>
            <p
              v-if="product.is_sanctioned"
              class="mt-3 rounded-lais-sm bg-red-50 px-3 py-2 text-[12px] text-red-700"
            >
              Este produto possui uma restrição informada pelo WhatsApp.
            </p>
          </div>
        </section>

        <section
          class="rounded-lais border border-lais-border-soft bg-white p-5 sm:p-6"
        >
          <div
            class="flex flex-col gap-3 border-b border-lais-border-soft pb-5 sm:flex-row sm:items-start sm:justify-between"
          >
            <div>
              <p
                v-if="product.retailer_id"
                class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                SKU {{ product.retailer_id }}
              </p>
              <h2 class="mt-1 text-[24px] font-semibold text-lais-text">
                {{ product.name }}
              </h2>
              <p class="mt-2 text-[24px] font-bold text-lais-primary">
                {{ catalogProductPrice(product) }}
              </p>
              <p
                v-if="product.sale_price_amount_1000"
                class="mt-1 text-[12px] text-lais-text-muted"
              >
                Preço promocional aplicado
              </p>
            </div>
            <span
              class="rounded-full bg-lais-bg-card px-3 py-1.5 text-[11px] font-semibold text-lais-text-body"
            >
              {{ product.currency || 'BRL' }}
            </span>
          </div>

          <div class="grid gap-5 py-5">
            <div>
              <p
                class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                Descrição
              </p>
              <p
                class="mt-2 whitespace-pre-line text-[14px] leading-6 text-lais-text-body"
              >
                {{ product.description || 'Nenhuma descrição cadastrada.' }}
              </p>
            </div>
            <div v-if="product.url" class="rounded-lais-sm bg-lais-bg-card p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                Link do produto
              </p>
              <a
                :href="product.url"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-2 block truncate text-[13px] font-semibold text-lais-link underline underline-offset-2"
              >
                {{ product.url }}
              </a>
            </div>
            <div class="grid gap-3 sm:grid-cols-3">
              <div class="rounded-lais-sm border border-lais-border-soft p-3">
                <p class="text-[11px] text-lais-text-muted">Disponibilidade</p>
                <p class="mt-1 text-[13px] font-semibold text-lais-text">
                  {{ product.availability || 'Não informada' }}
                </p>
              </div>
              <div class="rounded-lais-sm border border-lais-border-soft p-3">
                <p class="text-[11px] text-lais-text-muted">Visibilidade</p>
                <p class="mt-1 text-[13px] font-semibold text-lais-text">
                  {{ product.is_hidden ? 'Oculto' : 'Publicado' }}
                </p>
              </div>
              <div class="rounded-lais-sm border border-lais-border-soft p-3">
                <p class="text-[11px] text-lais-text-muted">Identificador</p>
                <p
                  class="mt-1 truncate text-[13px] font-semibold text-lais-text"
                >
                  {{ product.id }}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </LaisOperationalPage>

  <LaisConfirmModal
    :show="showDeleteConfirm"
    title="Excluir produto"
    :description="`Deseja excluir ${product?.name || 'este produto'} do catálogo? Essa ação não pode ser desfeita.`"
    confirm-text="Excluir produto"
    :is-loading="isSaving"
    icon="i-lucide-trash-2"
    @close="showDeleteConfirm = false"
    @confirm="confirmDelete"
  />
</template>
